import qs from 'qs';
import { renderToString as vueRenderToString } from 'vue/server-renderer';
import type { App, WatchStopHandle } from 'vue';
import removeIndexNameFromQuery from './removeIndexNameFromQuery';
import addIndexNameToQuery from './addIndexNameToQuery';
import getAdjustedQuery from '~~/helpers/typesense/typesenseSSR/getAdjustedQuery';
import { isSearchQueryParameter } from '~~/helpers/typesense/isSearchQueryParameter';
import { isSearchPageSlug } from '~~/helpers/routes/getPageInformation';
import filterRouteForLinks from '~~/helpers/typesense/typesenseSSR/filterRouteForLinks/filterRouteForLinks';
import type {
  TypesenseIndexState,
  CreateHydratorOptions,
  InitializeInstantSearchContextOptions,
  LoadTypesenseStateOptions,
  RegisterInstantSearchRenderListenerOptions,
  SetupClientHydrationWatcherOptions,
  SetupRouteSyncOptions,
} from '~~/types/search/TypesenseState';
import type { IndexUiState, UiState } from 'instantsearch.js';
import type { Router } from 'vue-router';
import { TYPESENSE_ATTRIBUTE, CATEGORIES_ATTRIBUTES, DEFAULT_QUERY_VALUES, QUERY_KEY } from '~~/constants/typesense';
import {
  connectPagination,
  connectSortBy,
  connectRatingMenu,
  connectRefinementList,
  connectRange,
  connectHierarchicalMenu,
} from 'instantsearch.js/es/connectors';
import {
  createServerRootMixin,
  AisInstantSearchSsr,
  AisHits,
  AisSearchBox,
  AisConfigure,
  AisStateResults,
  AisStats,
} from 'vue-instantsearch/vue3/es';
import { STATUS_RESOURCE_NOT_FOUND } from '~~/constants/http';

const isRefinementRouteKey = (key: string) => key.startsWith('refinementList[') ||
  key.startsWith('hierarchicalMenu[') ||
  key.startsWith('ratingMenu[');

// Two independent things call typesenseRouter.write(): our own registerInstantSearchRenderListener
// (below), AND instantsearch.js's own built-in routing middleware (see routing option passed
// into createServerRootMixin in initializeInstantSearchContext -> InstantSearch's constructor
// calls `this.use(createRouterMiddleware(...))`, whose onStateChange calls router.write() on
// every ui-state change). That built-in middleware has no awareness of query params already
// live in the URL, so on a fresh page/category mount it can call write() with an incomplete ui
// state (e.g. before a refinementList from the URL has been merged in) and wipe those params.
// Patching routeState here, in write() itself, protects against BOTH callers regardless of
// which one fires first.
const restoreRefinementParamsFromUrl = (
  routeState: Record<string, string | number>,
): Record<string, string | number> => {

  if (typeof window === 'undefined') {

    return routeState;

  }

  const hasRefinementsNow = Object.keys(routeState).some(isRefinementRouteKey);

  if (hasRefinementsNow) {

    return routeState;

  }

  const currentSearch = window.location.search.startsWith('?') ?
    window.location.search.slice(1) :
    window.location.search;

  const currentParams = qs.parse(currentSearch, { depth: 0 }) as Record<string, unknown>;
  const hadRefinementsBefore = Object.keys(currentParams).some(isRefinementRouteKey);

  if (!hadRefinementsBefore) {

    return routeState;

  }

  const patched = { ...routeState };

  Object.entries(currentParams).forEach(([key, value]) => {

    if (!isRefinementRouteKey(key) || key in patched) {

      return;

    }

    const normalizedValue = Array.isArray(value) ? value.find(entry => entry != null) ?? '' : value;

    if (normalizedValue !== undefined && normalizedValue !== null) {

      patched[key] = typeof normalizedValue === 'string' ? normalizedValue : String(normalizedValue);

    }

  });

  return patched;

};

export const typesenseRouter = (nuxtRouter: Router, isSSRMode = true, typesenseIndexName: string = '') => ({
  _lastPopstateAt: 0 as number,
  _onPopState: undefined as undefined | ((event: PopStateEvent) => void),
  writeTimer: undefined as undefined | number,
  read() {

    const windowLocation = (typeof window === 'object') ? window.location.href : '';
    const path = windowLocation || (nuxtRouter?.currentRoute?.value?.fullPath ?? '/');
    const search = path.includes('?') ? path.split('?')[1] : '';
    const searchQuery = qs.parse(search, { ignoreQueryPrefix: true, depth: 0 });
    const withIndex = addIndexNameToQuery(searchQuery, typesenseIndexName || '');

    return withIndex;

  },
  write(incomingRouteState: Record<string, string | number>) {

    const tempIsSSR = isSSRMode;
    isSSRMode = false;

    const currentPath = nuxtRouter?.currentRoute?.value?.path
    ?? nuxtRouter?.currentRoute?.value?.fullPath
    ?? '/';

    const effectivePath = (typeof window === 'object') ? window.location.pathname : currentPath;

    const isOnSearchPage = isSearchPageSlug(effectivePath);

    const routeState = restoreRefinementParamsFromUrl(incomingRouteState);

    const evaluation = (function getDefaultAndCheck(renderState: Record<string, unknown>) {

      let shouldClearQueryParams = true;

      if (renderState && typeof renderState === 'object') {

        for (const [_key, currentValue] of Object.entries(renderState)) {

          if (currentValue && typeof currentValue === 'string' && currentValue.trim() !== '') {

            shouldClearQueryParams = false;
            break;

          }

          if (typeof currentValue === 'number' && currentValue !== 0) {

            shouldClearQueryParams = false;
            break;

          }

          if (currentValue && typeof currentValue === 'object') {

            for (const nestedValue of Object.values(currentValue)) {

              if (nestedValue) {

                shouldClearQueryParams = false;
                break;

              }

            }

          }

          if (!shouldClearQueryParams) {

            break;

          }

        }

      }

      return {
        shouldClearQueryParams,
        currentRouteState: shouldClearQueryParams ? {} : renderState ?? {},
      };

    })(routeState);
    let { shouldClearQueryParams, currentRouteState } = evaluation;

    if (this.createURL(currentRouteState) === this.createURL(this.read()) && !shouldClearQueryParams) {

      return;

    }

    const existingQueryParam = (() => {

      const param = nuxtRouter?.currentRoute?.value?.query?.query;

      if (Array.isArray(param)) {

        return param.find((entry): entry is string => typeof entry === 'string' && entry.trim() !== '') ?? '';

      }

      return typeof param === 'string' ? param : '';

    })();

    if (shouldClearQueryParams && isOnSearchPage && existingQueryParam.trim() !== '') {

      currentRouteState = { ...currentRouteState, query: existingQueryParam };
      shouldClearQueryParams = false;

    }

    const routerForAdjust = {
      currentRoute: {
        query: (nuxtRouter?.currentRoute?.value?.query) || {},
      },
    };
    let adjustedQuery = getAdjustedQuery(
      routerForAdjust,
      currentRouteState,
      tempIsSSR,
      typesenseIndexName || '',
    );

    if ((!adjustedQuery || adjustedQuery === '') && !isOnSearchPage) {

      adjustedQuery = currentPath;

    }

    if (typeof adjustedQuery === 'string' && adjustedQuery.startsWith('?')) {

      adjustedQuery = `${currentPath.replace(/\/{2,}/g, '/')}${adjustedQuery}`;

    }
    const urlToUse = typeof adjustedQuery === 'string' && adjustedQuery ?
      adjustedQuery :
      this.createURL(currentRouteState);

    if (typeof history === 'object' && history.replaceState) {

      // Avoid overwriting browser navigation (back/forward) state immediately
      // after a popstate event — some consumers call write on popstate which
      // can cause the URL to revert while the router already navigated the
      // app view. If a popstate happened very recently, skip the replace.
      const now = Date.now();
      const globalLast = (typeof window !== 'undefined' &&
        ((window as unknown) as { __typesense_last_popstate_at__?: number }).__typesense_last_popstate_at__) || 0;
      const instanceLast = 0;
      const last = Math.max(globalLast, instanceLast);

      if (last && (now - last) < 200) {

        return;

      }

      const existingState = (typeof history === 'object' && history.state) ? history.state : null;
      history.replaceState(
        existingState && typeof existingState === 'object' ?
          { ...existingState, ...currentRouteState } :
          currentRouteState,
        '',
        urlToUse,
      );

    }

  },
  createURL(routeState: Record<string, unknown>) {

    const queryHolder: { [key: string]: string | Record<string, string> } = {};
    Object.entries(routeState).forEach(([key, currentValue]) => {

      if (typeof currentValue === 'object' && currentValue !== null) {

        queryHolder[key] = Object.fromEntries(
          Object.entries(currentValue).map(
            ([nestedKey, nestedValue]) => [nestedKey, String(nestedValue)],
          ),
        );

      } else if (currentValue !== undefined) {

        queryHolder[key] = String(currentValue);

      }

    });
    const cleaned = removeIndexNameFromQuery(queryHolder, typesenseIndexName || '');
    const filtered = filterRouteForLinks(cleaned);
    let filteredWithoutDefaults: Record<string, unknown> = { ...filtered };
    const preservedQueryParameters = Object.entries(nuxtRouter?.currentRoute?.value?.query ?? {}).reduce(
      (accumulator: Record<string, unknown>, [queryLabel, queryValue]) => {

        if (!isSearchQueryParameter(queryLabel) && queryValue !== undefined) {

          accumulator[queryLabel] = queryValue;

        }

        return accumulator;

      },
      {},
    );

    const normalizedSortBy = typeof filteredWithoutDefaults.sortBy === 'string'
      ? (filteredWithoutDefaults.sortBy).trim()
      : '';

    if (!normalizedSortBy || (typesenseIndexName && normalizedSortBy === typesenseIndexName)) {

      const { sortBy: _sortBy, ...rest } = filteredWithoutDefaults;
      filteredWithoutDefaults = rest;

    }

    const query = qs.stringify(
      {
        ...filteredWithoutDefaults,
        ...preservedQueryParameters,
      },
      { addQueryPrefix: true },
    );
    const basePath = nuxtRouter?.currentRoute?.value?.path
        ?? nuxtRouter?.currentRoute?.value?.fullPath
        ?? '/';

    const normalizedQuery = (basePath === '/search' && !query) ? '?query=' : query;

    const combined = `${basePath.replace(/\/{2,}/g, '/')}${normalizedQuery}`;

    return combined;

  },

  onUpdate(callback: (rs: Record<string, unknown>) => void) {

    if (typeof window === 'undefined') {

      return;

    }

    if (this._onPopState) {

      window.removeEventListener('popstate', this._onPopState);
      this._onPopState = undefined;

    }

    this._onPopState = () => {

      if (this.writeTimer) {

        window.clearTimeout(this.writeTimer);
        this.writeTimer = undefined;

      }
      // mark the time of the popstate so write() can avoid replacing state
      // immediately afterwards (global timestamp so multiple instances cooperate)
      try {

        ((window as unknown) as { __typesense_last_popstate_at__?: number }).__typesense_last_popstate_at__ = Date.now();

      } catch (e) {

        void e;

      }
      callback(this.read());

    };
    window.addEventListener('popstate', this._onPopState);

  },

  dispose() {

    if (this._onPopState && typeof window === 'object') {

      window.removeEventListener('popstate', this._onPopState);
      this._onPopState = undefined;

    }

    if (this.writeTimer && typeof window === 'object') {

      window.clearTimeout(this.writeTimer);
      this.writeTimer = undefined;

    }

  },
});

export const stateMapping = (indexName: string, defaultRefinements: Record<string, unknown> = {}) => ({
  stateToRoute(uiState: UiState): Record<string, string | number> {

    const routeState: Record<string, string | number> = {};
    const state = uiState?.[indexName] ?? {};
    const refinementList = state?.refinementList ?? {};
    const hierarchicalMenu: Record<string, string[]> = (state?.hierarchicalMenu) || {};
    const menu: Record<string, string> = state?.menu || {};

    const ratingMenu = state?.ratingMenu || {};
    const rangeMenu = state?.range || {};

    if (rangeMenu) {

      routeState['range[price]'] = rangeMenu.price;

    }
    for (const [attribute, values] of Object.entries(refinementList)) {

      const arr = Array.isArray(values) ? values : [];

      if (attribute === 'avgRating') {

        if (arr[0]) {

          routeState[`ratingMenu[${attribute}]`] = arr[0];

        }
        continue;

      }
      arr.forEach((val, idx) => {

        routeState[`refinementList[${attribute}][${idx}]`] = val;

      });

    }
    for (const [attribute, value] of Object.entries(hierarchicalMenu)) {

      (value || []).forEach((entry: string, idx: number) => {

        routeState[`hierarchicalMenu[${attribute}][${idx}]`] = String(entry);

      });

    }
    for (const [attribute, value] of Object.entries(menu)) {

      if (value) {

        routeState[`menu[${attribute}]`] = String(value);

      }

    }

    for (const [attribute, value] of Object.entries(ratingMenu)) {

      if (value) {

        routeState[`ratingMenu[${attribute}]`] = String(value);

      }

    }

    const hitsPerPageValue = typeof state.hitsPerPage === 'number' ?
      state.hitsPerPage :
      state.configure?.hitsPerPage;

    if (
      typeof hitsPerPageValue === 'number' &&
      hitsPerPageValue !== DEFAULT_QUERY_VALUES[QUERY_KEY.HITS_PER_PAGE]) {

      routeState.hitsPerPage = hitsPerPageValue;

    }


    if (typeof state?.sortBy === 'string') {

      routeState.sortBy = state.sortBy;

    }

    const queryValue = state?.query ?? '';

    if (queryValue && queryValue.trim() !== '') {

      routeState.query = queryValue;

    }

    const pageValue = state?.page;

    if (pageValue !== undefined && pageValue !== 0) {

      routeState.page = pageValue;

    }

    if (state?.configure?.filters) {

      routeState.filters = state.configure.filters;

    }

    return routeState;

  },

  routeToState(routeState: Record<string, Partial<TypesenseIndexState>>) {

    const indexState: TypesenseIndexState = {};
    const ratingMenu: Record<string, string> = {};
    const hierarchicalMenu: Record<string, string[]> = {};
    const hierarchicalMenuSegments: Record<string, string[]> = {};
    const refinementMenu: Record<string, string[]> = {};
    const rangeMenu: Record<string, string> = {};

    for (const [rawKey, defaultValue] of Object.entries(defaultRefinements)) {

      indexState[rawKey] = routeState[rawKey] ? [routeState[rawKey]] : [defaultValue ?? ''];

    }
    for (const [key, value] of Object.entries(routeState?.[indexName] || {})) {

      if (value == null || value === '') {

        continue;

      }

      const ratingMatch = key.match(/^ratingMenu\[(.+?)\]$/);

      if (ratingMatch) {

        const attr = ratingMatch[1];
        ratingMenu[attr] = String(value);
        continue;

      }
      const hierarchicalMatchIndexed = key.match(/^hierarchicalMenu\[(.+?)\]\[(\d+)\]$/);

      if (hierarchicalMatchIndexed) {

        const attr = hierarchicalMatchIndexed[1];
        const idx = Number(hierarchicalMatchIndexed[2]);

        if (!hierarchicalMenuSegments[attr]) {

          hierarchicalMenuSegments[attr] = [];

        }
        hierarchicalMenuSegments[attr][idx] = String(value);
        continue;

      }
      const refinementMatch = key.match(/^refinementList\[(.+?)\]\[(\d+)\]$/);

      if (refinementMatch) {

        const attr = refinementMatch[1];
        const index = Number(refinementMatch[2]);

        if (!refinementMenu[attr]) {

          refinementMenu[attr] = [];

        }
        refinementMenu[attr][index] = String(value);
        continue;

      }
      const rangeMatch = key.match(/^range\[(.+?)\]$/);

      if (rangeMatch) {

        const attr = rangeMatch[1];

        if (!rangeMenu[attr]) {

          rangeMenu[attr] = '';

        }
        rangeMenu[attr] = String(value);
        continue;

      }

    }
    for (const [attr, segments] of Object.entries(hierarchicalMenuSegments)) {

      hierarchicalMenu[attr] = [];
      segments.forEach((entry: string) => {

        hierarchicalMenu[attr].push(String(entry));

      });

    }

    if (Object.keys(ratingMenu).length) {

      indexState.ratingMenu = ratingMenu;

    }

    if (Object.keys(hierarchicalMenu).length) {

      indexState.hierarchicalMenu = hierarchicalMenu;

    } else {

      for (const [key, defaultValue] of Object.entries(defaultRefinements)) {


        if (Array.isArray(defaultValue)) {

          refinementMenu[key] = defaultValue;

        }

      }

    }


    if (Object.keys(refinementMenu).length) {

      indexState.refinementList = refinementMenu;

    }

    if (rangeMenu) {

      indexState.range = rangeMenu;

    }

    const sortByValue = routeState[indexName]?.sortBy;

    if (typeof sortByValue === 'string') {

      indexState.sortBy = String(sortByValue);

    }

    const pageValue = routeState[indexName]?.page;

    if (typeof pageValue === 'string' || typeof pageValue === 'number') {

      indexState.page = Number(pageValue);

    }

    const hitsPerPageValue = routeState[indexName]?.hitsPerPage;

    if (typeof hitsPerPageValue === 'string' || typeof hitsPerPageValue === 'number') {

      const hitsPerPage = Number(hitsPerPageValue);
      indexState.configure = { ...(indexState.configure || {}), hitsPerPage };

    }

    const queryValue = routeState[indexName]?.query;

    if (typeof queryValue === 'string') {

      indexState.query = queryValue;

    }
    const mappedState = { [indexName]: indexState };

    return mappedState;

  },
});

function renderToString(app: App | VNode): Promise<string> {

  return new Promise((resolve, reject) => {

    vueRenderToString(app).then(resolve).catch(reject);

  });

}

export function initializeInstantSearchContext({
  searchClient,
  indexName,
  router,
  stateMap,
}: InitializeInstantSearchContextOptions) {

  const routingConfig = router && stateMap
    ? {
      router,
      stateMapping: stateMap,
    }
    : undefined;

  const serverRootMixin = createServerRootMixin({
    searchClient,
    indexName,
    future: { preserveSharedStateOnUnmount: true },
    ...(routingConfig ? { routing: routingConfig } : {}),
  });

  const { instantsearch } = serverRootMixin.data();
  const ensureVirtualWidgets = () => {

    const instanceWithFlag = instantsearch as typeof instantsearch & { _hasVirtualWidgets?: boolean };

    if (instanceWithFlag._hasVirtualWidgets) {

      return;

    }

    const widgets = [
      connectPagination(() => null)({}),
      connectSortBy(() => null)({
        items: [
          { value: `${indexName}`, label: 'Default' },
          { value: `${indexName}_price_desc`, label: 'Price Desc' },
        ],
      }),
      connectRatingMenu(() => null)({ attribute: TYPESENSE_ATTRIBUTE.AVG_RATING }),
      connectRange(() => null)({ attribute: TYPESENSE_ATTRIBUTE.PRICE }),
      connectRefinementList(() => null)({ attribute: TYPESENSE_ATTRIBUTE.BRAND }),
      connectRefinementList(() => null)({ attribute: TYPESENSE_ATTRIBUTE.TAGS }),
      connectHierarchicalMenu(() => null)({ attributes: CATEGORIES_ATTRIBUTES }),
    ];

    instanceWithFlag.addWidgets(widgets);
    instanceWithFlag._hasVirtualWidgets = true;

  };

  return { instantsearch, ensureVirtualWidgets };

}

export function loadTypesenseState({
  asyncDataKey,
  instantsearch,
  nuxtApp,
  ensureVirtualWidgets,
  categoryFilter,
  hitsPerPage,
  indexName,
}: LoadTypesenseStateOptions) {

  const event = import.meta.server ? useRequestEvent() : null;

  return useAsyncData(asyncDataKey, async () => {

    if (import.meta.client) {

      const cachedState = nuxtApp.payload.data?.[asyncDataKey] ?? nuxtApp.static?.data?.[asyncDataKey];

      if (cachedState) {

        return cachedState;

      }

    }

    ensureVirtualWidgets();

    const resolvedHitsPerPage = typeof hitsPerPage?.value === 'number' && Number.isFinite(hitsPerPage.value)
      ? hitsPerPage.value
      : DEFAULT_QUERY_VALUES[QUERY_KEY.HITS_PER_PAGE];

    const typesenseState = await instantsearch.findResultsState({
      component: {
        $options: {
          components: {
            AisInstantSearchSsr,
            AisConfigure,
            AisHits,
            AisStats,
            AisSearchBox,
            AisStateResults,
          },
          data() {

            return { instantsearch };

          },
          provide: { $_ais_ssrInstantSearchInstance: instantsearch },
          render() {

            const configureProps: Record<string, unknown> = {
              hitsPerPage: resolvedHitsPerPage,
              clickAnalytics: true,
            };

            const categoryFilterValue = categoryFilter.value;

            if (categoryFilterValue) {

              configureProps.filters = categoryFilterValue;

            } else {

              configureProps.filters = undefined;

            }

            return h(
              AisInstantSearchSsr,
              null,
              () => [
                h(AisConfigure, configureProps),
                h(AisHits),
                h(AisStats),
                h(AisSearchBox),
                h(AisStateResults),
              ],
            );

          },
        },
      },
      renderToString,
    });

    // Check if the pagination is valid and set 404 status if needed
    if (import.meta.server && indexName && event) {

      const isInvalidPagination = checkTypesenseValidPageInPagination(typesenseState, indexName);

      if (isInvalidPagination) {

        setResponseStatus(event, STATUS_RESOURCE_NOT_FOUND);

      }

    }

    return typesenseState;

  });

}

/**
 * Check if the current Typesense page is beyond the available pages (nbPages)
 * Returns true if pagination is invalid (page > nbPages)
 */
export function checkTypesenseValidPageInPagination(typesenseState: any, indexName: string): boolean {

  const firstResult = typesenseState?.[indexName]?.results?.[0];

  if (!firstResult) {

    return false;

  }

  // Typesense page is 0-indexed, so page + 1 gives the current page number
  // If current page number exceeds total pages, it's invalid
  return firstResult.page + 1 > firstResult.nbPages;

}

export function createHydrator({
  instantsearch,
  typesenseState,
  ensureVirtualWidgets,
}: CreateHydratorOptions) {

  const hasHydrated = ref(false);

  return () => {

    if (hasHydrated.value) {

      return;

    }

    if (!typesenseState?.value) {

      return;

    }

    instantsearch.hydrate(typesenseState.value);
    const currentUiState = instantsearch.getUiState?.();

    if (currentUiState) {

      instantsearch.mainHelper?.setState?.(currentUiState);

    }

    ensureVirtualWidgets();

    hasHydrated.value = true;

  };

}

export function setupClientHydrationWatcher({
  typesenseState,
  hydrateInstantSearch,
}: SetupClientHydrationWatcherOptions) {

  if (typesenseState.value) {

    hydrateInstantSearch();

    return;

  }

  const stopHydrationWatch = watch(typesenseState, (value) => {

    if (!value) {

      return;

    }

    hydrateInstantSearch();
    stopHydrationWatch();

  });

}

export function setupRouteSync({
  instantsearch,
  routerForTypesense,
  stateMap,
  nuxtRouter,
  indexName,
}: SetupRouteSyncOptions) {

  let stopRouteWatch: WatchStopHandle | null = null;

  const syncInstantSearchWithRoute = () => {

    if (typeof window === 'undefined') {

      return;

    }

    const routeState = routerForTypesense.read();
    const routeIndexState = (routeState?.[indexName] ?? {}) as Record<string, Partial<TypesenseIndexState>>;
    const currentUiState = (instantsearch.getUiState?.() ?? {});
    const existingIndexState = (currentUiState?.[indexName] ?? {}) as Record<string, unknown>;
    const normalizedRouteState = {
      ...routeState as Record<string, Partial<TypesenseIndexState>>,
    };

    if (
      !Object.prototype.hasOwnProperty.call(normalizedRouteState, 'hitsPerPage') &&
      Object.prototype.hasOwnProperty.call(routeIndexState, 'hitsPerPage')
    ) {

      normalizedRouteState.hitsPerPage = routeIndexState.hitsPerPage;

    }

    if (
      !Object.prototype.hasOwnProperty.call(normalizedRouteState, 'filters') &&
      Object.prototype.hasOwnProperty.call(routeIndexState, 'filters')
    ) {

      normalizedRouteState.filters = routeIndexState.filters;

    }

    const mappedUiState = stateMap.routeToState(normalizedRouteState);
    const targetIndexState = { ...(mappedUiState?.[indexName] ?? {}) };
    const nextIndexState = targetIndexState as IndexUiState;

    const queryChanged =
      Object.prototype.hasOwnProperty.call(nextIndexState, 'query') &&
      nextIndexState.query !== existingIndexState?.query;

    if (queryChanged && !Object.prototype.hasOwnProperty.call(nextIndexState, 'page')) {

      nextIndexState.page = 0;

    }

    const nextUiState = {
      ...currentUiState,
      [indexName]: nextIndexState,
    } satisfies UiState;

    if (JSON.stringify(existingIndexState) === JSON.stringify(nextIndexState)) {

      return;

    }

    instantsearch.setUiState?.(() => nextUiState);
    instantsearch.mainHelper?.search?.();

  };

  const startRouteSync = () => {

    if (typeof window === 'undefined') {

      return;

    }

    const currentPath = nuxtRouter.currentRoute.value?.path ?? '/';

    if (isSearchPageSlug(currentPath)) {

      syncInstantSearchWithRoute();

    }

    stopRouteWatch = watch(
      () => nuxtRouter.currentRoute.value.fullPath,
      (newFullPath, oldFullPath) => {

        if (!newFullPath || newFullPath === oldFullPath) {

          return;

        }

        const updatedPath = nuxtRouter.currentRoute.value?.path ?? '/';

        if (!isSearchPageSlug(updatedPath)) {

          return;

        }

        syncInstantSearchWithRoute();

      },
    );

  };

  const stopRouteSync = () => {

    stopRouteWatch?.();
    stopRouteWatch = null;

  };

  return { startRouteSync, stopRouteSync };

}

export function registerInstantSearchRenderListener({
  instantsearch,
  stateMap,
  routerForTypesense,
  indexName,
  emit,
  shouldSyncRoute,
}: RegisterInstantSearchRenderListenerOptions) {

  const onRender = () => {

    if (typeof shouldSyncRoute === 'function' && !shouldSyncRoute()) {

      return;

    }

    const uiState = instantsearch.getUiState?.();

    if (!uiState) {

      return;

    }

    const indexState = (uiState[indexName] ?? {}) as Record<string, unknown>;
    const page = Number(indexState.page ?? 0);
    emit('page-changed', page);

    try {

      const mappedRouteState = restoreRefinementParamsFromUrl(stateMap.stateToRoute(uiState));

      routerForTypesense.write(mappedRouteState);

      try {

        const targetUrl = routerForTypesense.createURL(mappedRouteState);

        if (typeof window !== 'undefined') {

          const current = window.location.pathname + window.location.search;

          if (current !== targetUrl) {

            // Avoid overwriting browser navigation state immediately after a
            // popstate event. Use the global timestamp set by onUpdate popstate
            // handler to decide whether it's safe to replace the URL.
            const last = (typeof window !== 'undefined' &&
              ((window as unknown) as { __typesense_last_popstate_at__?: number }).__typesense_last_popstate_at__) || 0;

            if (!last || (Date.now() - last) > 200) {

              const existingState = (typeof history === 'object' && history.state) ? history.state : null;
              history.replaceState(
                existingState && typeof existingState === 'object' ?
                  { ...existingState, ...mappedRouteState } :
                  mappedRouteState,
                '',
                targetUrl,
              );

            }

          }

        }

      } catch (e2) {

        console.warn('[TypesenseContainer.render] fallback replaceState failed', e2);

      }

    } catch (e) {

      console.warn('[TypesenseContainer.render] router.write failed', e);

    }

  };

  instantsearch.on('render', onRender);

  return () => {

    instantsearch.removeListener('render', onRender);

  };

}

export const generateAsyncDataKey = (languageKey: string, categoryValue: string) => {

  const normalizedCategory = categoryValue || 'default';
  let hash = 0;

  for (let index = 0; index < normalizedCategory.length; index += 1) {

    const charCode = normalizedCategory.charCodeAt(index);
    hash = ((hash << 5) - hash) + charCode;
    hash |= 0;

  }

  const safeHash = Math.abs(hash).toString(36);

  return `typesense-state-${languageKey}-${safeHash}`;

};
